import { filter, map, take } from 'rxjs/operators';
import { CanActivateFn, Router } from '@angular/router';
import { Injectable, inject } from '@angular/core';
import { PermissionsService } from './permissions.service';

@Injectable()
export class RoutePermissionService {
  constructor(private permissions: PermissionsService) {}

  canActivate(licensePermissions: string[], permissions: string[], hasAccessWhenTeamResponsible?: boolean): boolean {
    let licensePermission = true;
    let generalPermission = true;
  
    if (licensePermissions?.length > 0) {
      licensePermission =
        this.permissions.checkLicensePermissions(licensePermissions, 'any');
    }

    if (hasAccessWhenTeamResponsible && licensePermission && this.permissions.isTeamResponsible) {
      return true; //ignore regular permissions when team responsible
    }

    if (permissions?.length > 0) {
      generalPermission = this.permissions.checkPermissions(permissions);
    }

    return licensePermission && generalPermission;
  }
}

/**
 * @description This function is used to check if the user has the required permissions to access a route.
 * @example
 * {
      path: 'chats',
      loadChildren: () =>
        import('../chats/chats.module').then((m) => m.ChatsModule),
      canActivate: [canActivateGeneral],
      runGuardsAndResolvers: 'always',
      data: {
        licensePermissions: ['CHATS.VIEW'],
        permissions: ['CHATS.VIEW'],
      },
    },
 * @param route
 * @returns Observable<boolean | UrlTree>
 */
export const canActivateGeneral: CanActivateFn = (route) => {
  const service = inject(RoutePermissionService);
  const router = inject(Router);
  return inject(PermissionsService).permissionsLoaded$
    .pipe(
      filter(Boolean),
      take(1),
      map(() => {
        const isAllowed = service.canActivate(
          route.data.licensePermissions,
          route.data.permissions,
          route.data.hasAccessWhenTeamResponsible,
        );

        if (!isAllowed) {
          return router.createUrlTree(['/']);
        }
        return isAllowed;
      }),
    );
};
